import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonicalUrl, 
  ogImage, 
  schemaData,
  cityName,
  stateName = 'California'
}) => {
  const defaultTitle = 'Pet Insurance California - Nationwide Coverage | Burbank Insurance Services';
  const defaultDescription = 'Get pet insurance in California. Nationwide coverage for dogs, cats & exotic pets. Compare plans, get quotes & protect your pets today.';
  const defaultKeywords = 'pet insurance california, dog insurance, cat insurance, nationwide pet insurance, burbank insurance services';

  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const siteUrl = 'https://burbankinsurance.com';
  
  // Generate local business schema if city data provided
  const localBusinessSchema = cityName ? {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency", 
    "name": "Burbank Insurance Services",
    "description": `Pet insurance agent serving ${cityName}, ${stateName}`,
    "url": siteUrl,
    "telephone": "+1-818-555-0123",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Main Street",
      "addressLocality": "Burbank",
      "addressRegion": "CA", 
      "postalCode": "91501",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "City",
      "name": cityName,
      "addressRegion": stateName
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Pet Insurance Plans",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Dog Insurance",
            "description": `Dog insurance coverage in ${cityName}, ${stateName}`
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Cat Insurance",
            "description": `Cat insurance coverage in ${cityName}, ${stateName}`
          }
        }
      ]
    }
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Burbank Insurance Services" />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl || siteUrl} />
      <meta property="og:site_name" content="Burbank Insurance Services" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Local Business Schema */}
      {localBusinessSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      
      {/* Additional Schema Data */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
      
      {/* Geographic Tags for Local SEO */}
      {cityName && (
        <>
          <meta name="geo.region" content={`US-CA`} />
          <meta name="geo.placename" content={`${cityName}, ${stateName}`} />
          <meta name="ICBM" content="34.0522,-118.2437" />
        </>
      )}
    </Helmet>
  );
};

export default SEOHead;